var App = App || {};

App.Initialization = App.Initialization || {};

App.Initialization['component.primaryNav'] = (function () {

    'use strict';

    const CONFIG = {
        SELECTOR_TRIGGERS: '.primaryNav__level2Trigger',
        SELECTOR_MENUS: '.primaryNav__megaMenu',
        SELECTOR_OVERLAY: '[data-primary-nav-overlay]',
        CLASS_OVERLAY_VISIBLE: 'is-visible',
        ANIMATION_DURATION: 250,
        ANIMATION_EASING: 'ease',
        TIMING_INTENT_DELAY: 250,
        TIMING_CLOSE_DELAY: 600,
        get animationTransition() {
            return `max-height ${this.ANIMATION_DURATION}ms ${this.ANIMATION_EASING}`;
        }
    };

    const State = {
        menuTimeout: null,
        currentMenu: null,
        interactionKeyboard: false,
        // set while Escape hands focus back to a trigger, so that focus doesn't immediately reopen the menu
        suppressFocusOpen: false,
        clearMenuTimeout() {
            clearTimeout(this.menuTimeout);
            this.menuTimeout = null;
        }
    };

    function initialize(nav) {
        const triggers = nav.querySelectorAll(CONFIG.SELECTOR_TRIGGERS);
        const megaMenus = nav.querySelectorAll(CONFIG.SELECTOR_MENUS);

        Helpers.setMegaMenuContentTabIndex(megaMenus, false);
        Events.bindMegaMenuEvents(triggers);
        Events.bindResettingEvents(nav, megaMenus);
    }

    const Events = {
        bindMegaMenuEvents(triggers) {
            triggers.forEach(trigger => {
                const triggerContainer = trigger.parentElement;
                const megaMenu = triggerContainer.querySelector(CONFIG.SELECTOR_MENUS);

                if (!megaMenu) {
                    return;
                }

                triggerContainer.addEventListener('mouseover', (event) => {
                    event.stopPropagation();
                    State.interactionKeyboard = false;
                    Handlers.megaMenuEngage(megaMenu);
                });

                triggerContainer.addEventListener('focusin', (event) => {
                    event.stopPropagation();

                    if (State.suppressFocusOpen) {
                        return;
                    }

                    State.interactionKeyboard = true;
                    Handlers.megaMenuEngage(megaMenu);
                });

                triggerContainer.addEventListener('mouseout', (event) => {
                    event.stopPropagation();

                    if (triggerContainer.contains(event.relatedTarget)) {
                        return;
                    }

                    Handlers.megaMenuDisengage(megaMenu);
                });

                triggerContainer.addEventListener('focusout', (event) => {
                    event.stopPropagation();

                    if (triggerContainer.contains(event.relatedTarget)) {
                        return;
                    }

                    Handlers.megaMenuDisengage(megaMenu);
                });

                triggerContainer.addEventListener('keydown', (event) => {
                    Handlers.triggerKeyDown(event, triggers, megaMenu);
                });
            });
        },

        bindResettingEvents(nav, megaMenus) {
            // WCAG 2.1.1 / 2.4.3: Escape closes the open menu and, if focus was inside it, returns focus to its trigger
            nav.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    const openMenu = State.currentMenu;
                    const trigger = Helpers.getMegaMenuTrigger(openMenu);
                    const focusWasInMenu = !!openMenu && openMenu.contains(document.activeElement);

                    Handlers.megaMenuAbandon(megaMenus);

                    if (trigger && focusWasInMenu) {
                        State.suppressFocusOpen = true;
                        trigger.focus();
                        State.suppressFocusOpen = false;
                    }
                }
            });

            // WCAG 1.4.13: a menu opened by hover can be dismissed with Escape without moving the pointer or focus
            document.addEventListener('keydown', (event) => {
                if (event.key !== 'Escape' || !State.currentMenu || !nav.contains(State.currentMenu) || nav.contains(document.activeElement)) {
                    return;
                }

                Handlers.megaMenuAbandon(megaMenus);
            });

            nav.addEventListener('mouseleave', () => {
                State.clearMenuTimeout();
                State.menuTimeout = setTimeout(() => {
                    Handlers.megaMenuAbandon(megaMenus);
                }, CONFIG.TIMING_CLOSE_DELAY);
            });
        }
    };

    const Handlers = {
        megaMenuEngage(megaMenu) {
            const intentDelay = State.interactionKeyboard ? 0 : CONFIG.TIMING_INTENT_DELAY;
            const switchingMenus = State.currentMenu && State.currentMenu !== megaMenu;
            const alreadyShowing = State.currentMenu === megaMenu;
            const animated = !switchingMenus && !alreadyShowing;

            State.clearMenuTimeout();

            State.menuTimeout = setTimeout(() => {
                if (switchingMenus) {
                    Helpers.setAriaExpanded(State.currentMenu, false);
                    Helpers.setMegaMenuContentTabIndex([State.currentMenu], false);
                    Animator.hide(State.currentMenu, animated);
                }

                Helpers.setAriaExpanded(megaMenu, true);
                Helpers.setMegaMenuContentTabIndex([megaMenu], true);
                Animator.show(megaMenu, animated);
                Helpers.setOverlayVisible(true);

                State.currentMenu = megaMenu;
            }, intentDelay);
        },

        megaMenuDisengage(megaMenu) {
            const intentDelay = State.interactionKeyboard ? 0 : CONFIG.TIMING_CLOSE_DELAY;

            State.clearMenuTimeout();

            State.menuTimeout = setTimeout(() => {
                Helpers.setAriaExpanded(megaMenu, false);
                Helpers.setMegaMenuContentTabIndex([megaMenu], false);
                Animator.hide(megaMenu, true);
                Helpers.setOverlayVisible(false);
                State.currentMenu = null;
            }, intentDelay);
        },

        megaMenuOpenNow(megaMenu) {
            State.clearMenuTimeout();
            State.interactionKeyboard = true;

            if (State.currentMenu === megaMenu) {
                return;
            }

            if (State.currentMenu) {
                Helpers.setAriaExpanded(State.currentMenu, false);
                Helpers.setMegaMenuContentTabIndex([State.currentMenu], false);
                Animator.hide(State.currentMenu, false);
            }

            Helpers.setAriaExpanded(megaMenu, true);
            Helpers.setMegaMenuContentTabIndex([megaMenu], true);
            Animator.show(megaMenu, false);
            Helpers.setOverlayVisible(true);
            State.currentMenu = megaMenu;
        },

        megaMenuAbandon(megaMenus) {
            State.clearMenuTimeout();
            State.currentMenu = null;
            State.interactionKeyboard = false;
            Helpers.setOverlayVisible(false);

            megaMenus.forEach(megaMenu => {
                Helpers.setAriaExpanded(megaMenu, false);
                Helpers.setMegaMenuContentTabIndex([megaMenu], false);
                megaMenu.removeAttribute('style');
            });
        },

        triggerKeyDown(event, triggers, megaMenu) {
            const megaMenuLinks = megaMenu?.querySelectorAll('a, button') ?? [];
            const isTrigger = [...triggers].includes(event.target);
            const isMegaMenuLink = [...megaMenuLinks].includes(event.target);

            if (!isTrigger && !isMegaMenuLink) {
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                event.stopPropagation();

                if (isTrigger) {
                    Helpers.keyDownFocusNextItem(event.target, triggers, 'next');
                    return;
                }

                if (isMegaMenuLink) {
                    const target = Helpers.getMegaMenuTrigger(megaMenu);
                    Helpers.keyDownFocusNextItem(target, triggers, 'next');
                }

                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();

                if (isTrigger) {
                    Helpers.keyDownFocusNextItem(event.target, triggers, 'prev');
                    return;
                }

                if (isMegaMenuLink) {
                    const target = Helpers.getMegaMenuTrigger(megaMenu);
                    Helpers.keyDownFocusNextItem(target, triggers, 'prev');
                }

                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();

                if (isTrigger && megaMenuLinks.length) {
                    // the menu may have been closed with Escape; hidden links can't take focus, so open it first
                    Handlers.megaMenuOpenNow(megaMenu);
                    megaMenuLinks[0].focus();
                    return;
                }

                if (isMegaMenuLink) {
                    Helpers.keyDownFocusNextItem(event.target, megaMenuLinks, 'next');
                }

                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();

                if (isMegaMenuLink) {
                    Helpers.keyDownFocusNextItem(event.target, megaMenuLinks, 'prev');
                }
            }
        }
    };

    const Helpers = {
        setMegaMenuContentTabIndex(megaMenus, enabled) {
            megaMenus.forEach(menu => {
                const items = menu.querySelectorAll('a, button');

                items.forEach(item => {
                    item.setAttribute('tabindex', enabled ? '0' : '-1');
                });
            });
        },

        setAriaExpanded(megaMenu, expanded) {
            const trigger = megaMenu?.closest('li')?.querySelector(':scope > .primaryNav__level2Trigger');

            if (!trigger) {
                return;
            }

            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        },

        keyDownFocusNextItem(target, items, direction) {
            let nextIdx = null;

            if (direction === 'next') {
                nextIdx = ([...items].indexOf(target) + 1) % items.length;
            }
            else if (direction === 'prev') {
                nextIdx = (([...items].indexOf(target) - 1) + items.length) % items.length;
            }

            if (nextIdx !== -1) {
                items[nextIdx].focus();
            }
        },

        getMegaMenuTrigger(megaMenu) {
            return megaMenu?.closest('li')?.querySelector(':scope > .primaryNav__level2Trigger');
        },

        setOverlayVisible(visible) {
            document.querySelector(CONFIG.SELECTOR_OVERLAY)?.classList.toggle(CONFIG.CLASS_OVERLAY_VISIBLE, visible);
        }
    };

    const Animator = {
        show(menu, animate = true) {
            menu.style.overflow = 'hidden';
            menu.style.display = 'block';
            menu.style.visibility = 'visible';
            menu.style.transition = CONFIG.animationTransition;

            if (!animate) {
                Animator.showInstant(menu);
                return;
            }

            Animator.showAnimatedBegin(menu);

            menu.addEventListener('transitionend', function handler() {
                Animator.showAnimatedEnd(menu);
                menu.removeEventListener('transitionend', handler);
            });

            menu.addEventListener('transitionend', Animator.showAnimatedEnd);
        },

        showInstant(menu) {
            menu.style.maxHeight = menu.scrollHeight + 'px';
            menu.style.transition = 'none';
            menu.style.maxHeight = 'none';
            menu.style.height = 'auto';
        },

        showAnimatedBegin(menu) {
            menu.style.maxHeight = '0';
            void menu.offsetHeight;
            menu.style.maxHeight = menu.scrollHeight + 'px';
        },

        showAnimatedEnd(menu) {
            menu.style.maxHeight = 'none';
            menu.style.height = 'auto';
            menu.removeEventListener('transitionend', Animator.showAnimatedEnd);
        },

        hide(menu, animate = true) {
            menu.style.overflow = 'hidden';
            menu.style.transition = CONFIG.animationTransition;

            if (!animate) {
                Animator.hideInstantStyles(menu);
                return;
            }

            Animator.hideAnimatedBegin(menu);

            menu.addEventListener('transitionend', function handler() {
                Animator.hideAnimatedEnd(menu);
                menu.removeEventListener('transitionend', handler);
            });

            menu.addEventListener('transitionend', Animator.hideAnimatedEnd);
        },

        hideInstantStyles(menu) {
            menu.removeAttribute('style');
        },

        hideAnimatedBegin(menu) {
            menu.style.maxHeight = menu.scrollHeight + 'px';
            void menu.offsetHeight;
            menu.style.maxHeight = '0';
        },

        hideAnimatedEnd(menu) {
            menu.removeAttribute('style');
            menu.removeEventListener('transitionend', Animator.hideAnimatedEnd);
        }
    };

    return {
        initialize
    };

})();